/** D24 — Saras iQ setup wizard entry context (query + session mirror). */

import { environment } from '../../../environments/environment';

export type IqSetupStep = 'warehouse' | 'sources';

const IQ_SETUP_KEY = 'iq_setup';
const IQ_SETUP_STEP_KEY = 'iq_setup_step';
const IQ_SETUP_RETURN_PATH_KEY = 'iq_setup_return_path';
const IQ_SETUP_WAREHOUSE_OPENED_KEY = 'iq_setup_warehouse_opened';
const IQ_SETUP_RETURN_TO_IQ_KEY = 'iq_setup_return_to_iq';
const IQ_RETURN_PARAM = 'iq_return';
const IQ_RETURN_PATH = '/setup';
const IQ_RETURN_ROOT_HOST = 'sarasanalytics.com';
const IQ_RETURN_HOST_SUFFIX = `.${IQ_RETURN_ROOT_HOST}`;

/**
 * Checks if the hostname is a valid Saras domain (apex or any subdomain).
 * Only the origin plus `/chat` is ever used for navigation.
 */
function isSarasHost(hostname: string): boolean {
  return hostname === IQ_RETURN_ROOT_HOST || hostname.endsWith(IQ_RETURN_HOST_SUFFIX);
}

/**
 * Accept an iQ return address only when it is the iQ setup page on a Saras host (or
 * localhost outside production), so a crafted link can never turn the "Continue in iQ"
 * button into an open redirect. Returns the normalised URL, else null.
 */
export function sanitizeIqReturnUrl(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.pathname !== IQ_RETURN_PATH || url.username || url.password) {
    return null;
  }
  const isSarasHttps = url.protocol === 'https:' && isSarasHost(url.hostname);
  const isLocalDev = !environment.production && url.protocol === 'http:' && url.hostname === 'localhost';
  return isSarasHttps || isLocalDev ? `${url.origin}${url.pathname}` : null;
}

function readUrlParams(): URLSearchParams {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }
  return new URLSearchParams(window.location.search);
}

/**
 * Params carrying ``iq_setup``: the page URL, or the ``redirectTo`` destination when iQ hands
 * off through ``/auth`` (there the setup params sit inside ``redirectTo``, not on ``/auth``).
 */
function readSetupSource(): { params: URLSearchParams; path: string } {
  const pageParams = readUrlParams();
  const own = {
    params: pageParams,
    path: `${window.location.pathname}${window.location.search}`,
  };
  if (pageParams.get(IQ_SETUP_KEY) === '1') {
    return own;
  }
  const redirectTo = pageParams.get('redirectTo');
  if (!redirectTo) {
    return own;
  }
  try {
    const target = new URL(redirectTo, window.location.origin);
    if (target.origin !== window.location.origin) {
      return own;
    }
    return { params: target.searchParams, path: `${target.pathname}${target.search}` };
  } catch {
    return own;
  }
}

/** Persist ``iq_setup`` params from the current URL (or its ``redirectTo``) into sessionStorage. */
export function captureIqSetupFromWindow(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  const { params, path } = readSetupSource();
  if (params.get(IQ_SETUP_KEY) !== '1') {
    return;
  }
  sessionStorage.setItem(IQ_SETUP_KEY, '1');
  const step = params.get(IQ_SETUP_STEP_KEY);
  if (step === 'warehouse' || step === 'sources') {
    sessionStorage.setItem(IQ_SETUP_STEP_KEY, step);
  } else {
    // No step (or an unknown one) on a new entry: don't carry the last entry's step over.
    sessionStorage.removeItem(IQ_SETUP_STEP_KEY);
  }
  sessionStorage.setItem(IQ_SETUP_RETURN_PATH_KEY, path);
  // Only ever set here: later captures that keep `iq_setup` but drop `iq_return` must not erase it.
  const iqReturn = sanitizeIqReturnUrl(params.get(IQ_RETURN_PARAM));
  if (iqReturn) {
    sessionStorage.setItem(IQ_SETUP_RETURN_TO_IQ_KEY, iqReturn);
  }
  // A fresh entry from iQ: let the warehouse step open its warehouse once more.
  sessionStorage.removeItem(IQ_SETUP_WAREHOUSE_OPENED_KEY);
}

/** Drop iQ setup session keys (call when the user leaves the Daton connectors hub). */
export function clearIqSetupContext(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  sessionStorage.removeItem(IQ_SETUP_KEY);
  sessionStorage.removeItem(IQ_SETUP_STEP_KEY);
  sessionStorage.removeItem(IQ_SETUP_RETURN_PATH_KEY);
  sessionStorage.removeItem(IQ_SETUP_WAREHOUSE_OPENED_KEY);
  sessionStorage.removeItem(IQ_SETUP_RETURN_TO_IQ_KEY);
}

function normalizePath(pathOrUrl: string): string {
  const [path] = (pathOrUrl || '').split(/[?#]/);
  return path.trim() || '/';
}

/** Daton connector routes iQ may open during setup (warehouse or sources step). */
export function isDatonIqSetupPath(pathOrUrl: string, baseWord: string): boolean {
  const path = normalizePath(pathOrUrl);
  const tenantPrefix = `/${baseWord}/`;
  if (!path.startsWith(tenantPrefix)) {
    return false;
  }
  const rest = path.substring(tenantPrefix.length);
  const warehousePrefix = 'warehouse/';
  if (rest.startsWith(warehousePrefix)) {
    const warehouseRest = rest.substring(warehousePrefix.length);
    // `<id>`, or `type/<TYPE>` which WarehouseTypeGuard resolves to an id.
    return /^\d+$/.test(warehouseRest) || /^type\/[A-Za-z0-9_]+$/.test(warehouseRest);
  }
  if (
    rest.startsWith('add-source-new/')
    || rest.startsWith('add-studio-source/')
    || rest.startsWith('edit-integration-new/')
    || rest.startsWith('edit-studio-source/')
    || rest.startsWith('edit-warehouse/')
    || rest.startsWith('integration-details/')
  ) {
    return true;
  }
  const connectorsPrefix = 'connectors/';
  if (!rest.startsWith(connectorsPrefix)) {
    return false;
  }
  const connectorsRest = rest.substring(connectorsPrefix.length);
  return (
    connectorsRest === 'datawarehouses-listing'
    || connectorsRest === 'datasources-listing'
    || connectorsRest.startsWith('datasources-listing/')
  );
}

/** Clear a stale iQ setup flag when navigation leaves allowed Daton connector routes. */
export function maybeClearStaleIqSetupContext(currentUrl: string, baseWord: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  if (sessionStorage.getItem(IQ_SETUP_KEY) !== '1') {
    return;
  }
  if (readUrlParams().get(IQ_SETUP_KEY) === '1') {
    return;
  }
  if (isDatonIqSetupPath(currentUrl, baseWord)) {
    return;
  }
  clearIqSetupContext();
}

/** Read-only: true when iQ setup is active via session mirror or current URL. */
export function isIqSetupActive(): boolean {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return false;
  }
  if (sessionStorage.getItem(IQ_SETUP_KEY) === '1') {
    return true;
  }
  return readUrlParams().get(IQ_SETUP_KEY) === '1';
}

/** Return the active iQ setup step ('warehouse' or 'sources') from sessionStorage or URL params. */
export function getIqSetupStep(): IqSetupStep | null {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }
  const fromSession = sessionStorage.getItem(IQ_SETUP_STEP_KEY);
  if (fromSession === 'warehouse' || fromSession === 'sources') {
    return fromSession;
  }
  const fromUrl = readUrlParams().get(IQ_SETUP_STEP_KEY);
  if (fromUrl === 'warehouse' || fromUrl === 'sources') {
    return fromUrl;
  }
  return null;
}

/** True when iQ sent the user to configure their warehouse (Step 1). */
export function isIqSetupWarehouseStep(): boolean {
  return isIqSetupActive() && getIqSetupStep() === 'warehouse';
}

/**
 * True once the warehouse step has opened its warehouse for this iQ setup entry, so going
 * back to the destinations listing shows the listing instead of opening it again.
 */
export function isIqSetupWarehouseOpened(): boolean {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return false;
  }
  return sessionStorage.getItem(IQ_SETUP_WAREHOUSE_OPENED_KEY) === '1';
}

/** Record that the warehouse step opened its warehouse (see `isIqSetupWarehouseOpened`). */
export function markIqSetupWarehouseOpened(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  sessionStorage.setItem(IQ_SETUP_WAREHOUSE_OPENED_KEY, '1');
}

/** Post-login / in-app navigation target when arriving from iQ setup. */
export function getIqSetupNavigateUrl(baseWord: string): string {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    const stored = sessionStorage.getItem(IQ_SETUP_RETURN_PATH_KEY);
    if (stored && stored.startsWith('/') && !stored.startsWith('//') && isDatonIqSetupPath(stored, baseWord)) {
      return stored;
    }
  }
  const step = getIqSetupStep();
  if (step === 'warehouse') {
    return `/${baseWord}/connectors/datawarehouses-listing`;
  }
  return `/${baseWord}/connectors/datasources-listing`;
}

/** Where "Continue in iQ" goes: the validated iQ setup URL while iQ setup is active, else null. */
export function getIqSetupReturnUrl(): string | null {
  if (!isIqSetupActive() || typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }
  const stored = sanitizeIqReturnUrl(sessionStorage.getItem(IQ_SETUP_RETURN_TO_IQ_KEY));
  if (stored) {
    return stored;
  }
  // Fall back to the `iq_return` inside the setup URL iQ opened, which is stored whole.
  const entry = sessionStorage.getItem(IQ_SETUP_RETURN_PATH_KEY);
  const query = entry && entry.includes('?') ? entry.slice(entry.indexOf('?') + 1) : '';
  return sanitizeIqReturnUrl(new URLSearchParams(query).get(IQ_RETURN_PARAM));
}

/** Dialog button type for the return-to-iQ action (meta carries the validated iQ URL). */
export const IQ_RETURN_BUTTON_TYPE = 'iq-return' as const;

export interface DialogButton {
  title: string;
  meta: string;
  type: 'redirect' | 'no-change' | typeof IQ_RETURN_BUTTON_TYPE;
  class: string;
}

/**
 * "Continue in iQ" dialog buttons for a success dialog, or an empty list when the user did
 * not come from iQ. Spread into the dialog's `buttons` array.
 *
 * @param isEdit - True after editing an existing source or warehouse: the button then reads
 *   "Back to iQ", since there is no setup progress to continue.
 */
export function iqReturnDialogButtons(isEdit = false): DialogButton[] {
  const url = getIqSetupReturnUrl();
  if (!url) {
    return [];
  }
  return [{
    title: isEdit ? 'Back to iQ' : 'Continue in iQ',
    meta: url,
    type: IQ_RETURN_BUTTON_TYPE,
    class: 'confirm-dialog-proceed',
  }];
}

/**
 * Builds the buttons list for success dialogs opened from iQ or normal flows.
 * If iQ setup is active, offers "Continue in iQ" (or "Back to iQ") alongside "Stay in Daton".
 * Otherwise returns the default buttons.
 */
export function buildIqAwareButtons(
  stayMeta: string,
  defaultButtons: DialogButton[],
  isEdit = false,
  stayType: 'redirect' | 'no-change' = 'redirect',
): DialogButton[] {
  const iqReturnButtons = iqReturnDialogButtons(isEdit);
  if (!iqReturnButtons.length) {
    return defaultButtons;
  }
  return [
    ...iqReturnButtons,
    {
      title: 'Stay in Daton',
      meta: stayMeta,
      type: stayType,
      class: 'confirm-dialog-later',
    },
  ];
}

/** Checks whether a dialog result represents the return-to-iQ button click. */
export function isIqReturnResult(result?: { type?: string }): boolean {
  return result?.type === IQ_RETURN_BUTTON_TYPE;
}

/**
 * Resolves the "Continue in iQ" or "Back to iQ" button for source configuration flows.
 * Returns dialog buttons only when the source flow is creating or editing a source during iQ setup.
 * Templates, attach, and clones get no return buttons.
 */
export function iqReturnButtonsForSource(purposeType: string, mode: string): DialogButton[] {
  const isEditingSource = purposeType === 'source' && mode === 'edit';
  const isNewSource = purposeType === 'source' && mode === 'create';
  return isNewSource || isEditingSource ? iqReturnDialogButtons(isEditingSource) : [];
}

/** iQ's own entry route; it decides whether the user goes to the setup page or the chat. */
const IQ_CHAT_PATH = '/chat';

/**
 * How "Continue in iQ" leaves this tab: when the tab that opened it (iQ) is still there, focus that
 * tab and close this one; otherwise open iQ's `/chat` here, which routes to setup or chat.
 *
 * @param returnUrl - Validated iQ setup URL (see `sanitizeIqReturnUrl`).
 * @param hasLiveOpener - Whether `window.opener` exists and is not closed.
 */
export function planIqReturn(
  returnUrl: string,
  hasLiveOpener: boolean,
): { mode: 'focus-and-close' | 'navigate'; chatUrl: string } {
  return {
    mode: hasLiveOpener ? 'focus-and-close' : 'navigate',
    chatUrl: new URL(IQ_CHAT_PATH, returnUrl).toString(),
  };
}

/**
 * Leave for iQ, clearing the setup context so management flows stay unaffected. Back to the iQ tab
 * that opened this one and close this tab if it is still open, else go to iQ's `/chat` in this tab.
 *
 * @param iqSetupUrl - Validated iQ setup URL (see `sanitizeIqReturnUrl`). Note that only the origin
 *   plus `/chat` is used for navigation.
 */
export function returnToIq(iqSetupUrl: string): void {
  clearIqSetupContext();
  let hasLiveOpener = false;
  try {
    hasLiveOpener = !!window.opener && !window.opener.closed;
  } catch {
    // A cross-origin opener can still be focused below; treat an unreadable one as gone.
  }
  const plan = planIqReturn(iqSetupUrl, hasLiveOpener);
  if (plan.mode === 'focus-and-close') {
    try {
      window.opener.focus();
    } catch {
      // Focus is best effort; the close below still returns the user to the iQ tab.
    }
    window.close();
    // A tab the browser refuses to close (not opened by script) must not strand the user.
    setTimeout(() => window.location.assign(plan.chatUrl), 300);
    return;
  }
  window.location.assign(plan.chatUrl);
}
